import type { IArticleConfig } from '~/config/article';
import widgetsConfig from '~/config/widgets.config';
import { createMarker, debugLog, markers, measures, measureTimeBetweenMarks } from '~/debug';
import CommunicationCreator from '~/modules/CommunicationCreator';
import { loadJS, waitForElement } from '~/utils/dom';
import { advancedKeys } from '~/config/keys';
import type CustomText from './CustomText';
import type { IIframeInterface } from './iframes/Iframe';
import Session from './Session';
import type { IAdvancedConfig } from './Advertisement/types';
import ServerLogger from './ServerLogger';
import AdUnitCreator from './Advertisement/AdUnitCreator';
import type { IIframelessWidgetInterface } from './IframelessElements/IframelessWidget';
import Comments from './iframes/Comments';
import IframelessComments from './IframelessElements/Comments';
import QuizIframe from './iframes/Quiz';
import EmotesIframe from './iframes/Emotes';

// For typescript typings

class WidgetsCreator {
  /** Store initialized IDs list to don't create widgets again */
  private static initializedIDs: string[] = [];
  /** Article config using which we create widgets */
  private readonly articleConfig: IArticleConfig;
  /** Article Index using which we create widgets */
  private readonly articleIndex: string;
  /**
   * Store each iFrame widget for further manipulations
   * ---
   * 📝 Note that for each individual article it's possible to have only
   * one comments/modal widget, and only one emotes widget. They are organized into set here too
   * to keep everything similar
   * ---
   */
  private modal: Map<HTMLDivElement, IIframeInterface> = new Map();
  private comments: Map<HTMLDivElement, IIframeInterface> = new Map();
  private iframelessComments: Map<HTMLDivElement, IIframelessWidgetInterface> = new Map();
  private emotes: Map<HTMLDivElement, IIframeInterface> = new Map();
  private quiz: Map<HTMLDivElement, IIframeInterface> = new Map();
  private powerbars: Map<HTMLDivElement, IIframeInterface> = new Map();
  private verticalPowerbars: Map<HTMLDivElement, IIframeInterface> = new Map();
  private subscribeWidgets: Map<HTMLDivElement, IIframeInterface> = new Map();
  private audioWidgets: Map<HTMLDivElement, IIframeInterface> = new Map();

  public communicationCreator: CommunicationCreator;
  private customText: CustomText;

  /**
   * @param {string} articleIndex - index of elements to add. One page can have several articles (inifinite scroll), based on that index we define selectors.
   * By default we set '' to display widgets if published doesn't have infinite scrolling or SPA.
   * @param {IArticleConfig} articleConfig article config
   * @param {[HTMLDivElement, IIframeInterface] | undefined} commentsIframe comments div and iframe
   * @param {[HTMLDivElement, IIframelessWidgetInterface] | undefined} iframelessComments comments div and iframe
   * @param {[HTMLDivElement, IIframeInterface] | undefined} emotesIframe emotes div and iframe
   * @param {[HTMLDivElement[], IIframeInterface[]] | []} sharebars sharebar divs and iframes
   * @param {[HTMLDivElement[], IIframeInterface[]] | []} subscribeWidgets subscribe widget divs and iframes
   * @param {[HTMLDivElement[], IIframeInterface[]] | []} audioWidgets audio widget divs and iframes
   * @param {[HTMLDivElement, IIframeInterface] | undefined} quizWidget quiz div and iframe
   */
  private constructor(
    articleIndex: string,
    articleConfig: IArticleConfig,
    commentsIframe: [HTMLDivElement, IIframeInterface] | undefined,
    iframelessComments: [HTMLDivElement, IIframelessWidgetInterface] | undefined,
    emotesIframe: [HTMLDivElement, IIframeInterface] | undefined,
    sharebars: [HTMLDivElement[], IIframeInterface[]] | [],
    subscribeWidgets: [HTMLDivElement[], IIframeInterface[]] | [],
    audioWidgets: [HTMLDivElement[], IIframeInterface[]] | [],
    quizWidget: [HTMLDivElement, IIframeInterface] | undefined
  ) {
    this.setWidgetInstances(
      commentsIframe,
      iframelessComments,
      emotesIframe,
      sharebars,
      subscribeWidgets,
      audioWidgets,
      quizWidget
    );

    /** 🔧 Set Article Config */
    this.articleConfig = articleConfig;
    /** 🔧 Set Article Index */
    this.articleIndex = articleIndex;

    /** 🔧 Start the Job */
    /** Add iFrame widgets available inside Maps to the DOM */
    scheduler
      .postTask(() => {
        this.initializeInsideDom();
      })
      .then(() => {
        scheduler.postTask(async () => {
          const customText = await import(/* webpackChunkName: "cst" */ './CustomText');
          // Pass the widgets for loading custom translation data if any
          this.customText = new customText.default(
            [...this.comments.values()][0],
            [...this.iframelessComments.values()][0],
            [...this.emotes.values()][0],
            [...this.powerbars.values(), ...this.verticalPowerbars.values()],
            [...this.quiz.values()][0],
            this.articleConfig
          );
        });
      });

    scheduler.postTask(() => {
      /** ⚠ Add non-iframe widgets */
      if (articleIndex === '') {
        // For now NewsFeed widget supports only non-indexed selectors
        WidgetsCreator.addNewsfeedToDOM(articleIndex);
        WidgetsCreator.addTrafficExchangeToDOM(articleIndex, this.articleConfig.apiKey);
      }
    });

    debugLog('log', `[VUUKLE] Widgets of id '${articleIndex.substring(1)}' are generated!`);
  }

  private setWidgetInstances(
    commentsIframe: [HTMLDivElement, IIframeInterface] | undefined,
    iframelessComments: [HTMLDivElement, IIframelessWidgetInterface] | undefined,
    emotesIframe: [HTMLDivElement, IIframeInterface] | undefined,
    sharebars: [HTMLDivElement[], IIframeInterface[]] | [],
    subscribeWidgets: [HTMLDivElement[], IIframeInterface[]] | [],
    audioWidgets: [HTMLDivElement[], IIframeInterface[]] | [],
    quizWidget: [HTMLDivElement, IIframeInterface] | undefined
  ) {
    if (!!commentsIframe) {
      this.comments.set(commentsIframe[0], commentsIframe[1]);
    }

    if (!!iframelessComments) {
      this.iframelessComments.set(iframelessComments[0], iframelessComments[1]);
    }

    if (!!emotesIframe) {
      this.emotes.set(emotesIframe[0], emotesIframe[1]);
    }

    if (sharebars[0] && sharebars[1]) {
      sharebars[0].forEach((div, index) => {
        if (sharebars[1] && sharebars[1][index]) {
          this.powerbars.set(div, sharebars[1][index]);
        }
      });
    }

    if (subscribeWidgets[0] && subscribeWidgets[1]) {
      subscribeWidgets[0].forEach((div, index) => {
        if (subscribeWidgets[1] && subscribeWidgets[1][index]) {
          this.subscribeWidgets.set(div, subscribeWidgets[1][index]);
        }
      });
    }

    if (audioWidgets[0] && audioWidgets[1]) {
      audioWidgets[0].forEach((div, index) => {
        if (audioWidgets[1] && audioWidgets[1][index]) {
          this.audioWidgets.set(div, audioWidgets[1][index]);
        }
      });
    }

    if (!!quizWidget) {
      this.quiz.set(quizWidget[0], quizWidget[1]);
    }
  }

  private setCommunicationCreatorInstance(communicationCreator: CommunicationCreator) {
    this.communicationCreator = communicationCreator;
  }

  /**
   * Make new widgets creator instance.
   * @param {string} articleIndex article index
   * @param {IArticleConfig} articleConfig article config
   * @param {RealtimeAnalytics} realtimeObj Realtime Analytics module
   * @returns {Promise<WidgetsCreator>}
   */
  public static async createWidgetsCreatorInstance(
    articleIndex: string,
    articleConfig: IArticleConfig
  ): Promise<WidgetsCreator> {
    // Check if widgets with the same ID were already generated.
    // If so - prevent them from being generated again, if not - proceed to generate widgets.
    if (articleIndex && WidgetsCreator.initializedIDs.indexOf(articleIndex) >= 0) {
      throw new Error('⚠ Widgets already have been created for this ID.');
    } else {
      if (articleIndex) {
        WidgetsCreator.initializedIDs.push(articleIndex);
      }
    }
    createMarker(markers.widgetsCreatorStart);
    measureTimeBetweenMarks(measures.platformStartToWidgetsStart, markers.platformStart, markers.widgetsCreatorStart);
    /** 🔧 Start the Job */
    const comments = await this.createCommentsWidget(articleIndex, articleConfig);
    const iframelessComments = await this.createIframelessCommentsWidget(articleIndex, articleConfig);
    const emotes = this.createEmotesIframe(articleIndex, articleConfig);
    /**
     * For powerbar we also need to detect widget standalone mode (if comments widget is on page, if emotes widget is on page).
     * @see standaloneModeType inside {@link ~/modules/iframes/Powerbar}
     */
    const sharebars = await this.createPowerbars(
      articleIndex,
      !!comments?.[1] || !!iframelessComments?.[1],
      !!emotes?.[1],
      articleConfig
    );
    const subscribeWidgets = await this.createSubscribeWidgets(articleIndex, articleConfig);
    const audioWidgets = await this.createAudioWidgets(articleIndex, articleConfig);
    const quiz = await this.createQuizWidget(articleIndex, articleConfig);

    const widgetsCreator = new WidgetsCreator(
      articleIndex,
      articleConfig,
      comments,
      iframelessComments,
      emotes,
      sharebars,
      subscribeWidgets,
      audioWidgets,
      quiz
    );

    const communicationCreator = await CommunicationCreator.createCommunicationCreatorInstance(
      articleIndex,
      comments?.[1],
      iframelessComments?.[1],
      emotes?.[1],
      sharebars[1] ? sharebars[1] : [],
      subscribeWidgets[1] ? subscribeWidgets[1] : [],
      audioWidgets[1] ? audioWidgets[1] : [],
      quiz?.[1],
      articleConfig,
      widgetsCreator
    );

    widgetsCreator.setCommunicationCreatorInstance(communicationCreator);

    createMarker(markers.widgetsCreatorEnd);
    measureTimeBetweenMarks(measures.widgetStartToEnd, markers.widgetsCreatorStart, markers.widgetsCreatorEnd);
    measureTimeBetweenMarks(measures.platformStartToWidgetsEnd, markers.platformStart, markers.widgetsCreatorEnd);
    return widgetsCreator;
  }

  /**
   * Get the place where we should insert the widget. Usually used for endless mode
   * @param selector
   * @param widgetSelector
   * @param usesClass
   * @param articleNumber
   */
  private static getLocation = (
    selector: string,
    widgetSelector: string,
    usesClass: boolean = false,
    articleNumber: number
  ) => {
    const match = selector.startsWith('#')
      ? document.querySelector(selector)
      : document.querySelectorAll(selector)[articleNumber - 1];
    if (!match && articleNumber - 1 !== 0) {
      /**
       * There can be situations where for example out of 5 articles, we can have less than 5 matches on querySelectorAll
       * With this loop we're checking previous querySelectorAll matches and selecting them
       * while making sure that there was no widget generated already
       */
      for (let i = articleNumber - 1; i > 0; i--) {
        let element = document.querySelectorAll(selector)[i];
        if (
          element &&
          !element.parentElement?.querySelector(
            usesClass ? `div[class*=${widgetSelector}]` : `div[id*=${widgetSelector}]`
          )
        ) {
          return element;
        }
      }
    } else {
      return match;
    }
    return null;
  };

  /**
   * Get the place where we should insert the widget
   * @param selector
   * @param windowContext
   * @returns
   */
  private getElementLocation = (selector: string, windowContext: Window = window): HTMLElement | null => {
    const match = windowContext.document.querySelectorAll<HTMLElement>(selector)[AdUnitCreator.articleCounter - 1];
    // When in endless mode, we want to select the nth match of the selector,
    // if we're generating ads for 3rd article we should pick the selector match at index of 2
    if (!match && AdUnitCreator.articleCounter - 1 !== 0) {
      /**
       * There can be situations where for example out of 5 articles, we can have less than 5 matches on querySelectorAll
       * With this loop we're checking previous querySelectorAll matches and selecting them
       * while making sure that there was no ad generated already
       */
      for (let i = AdUnitCreator.articleCounter - 1; i > 0; i--) {
        let element = windowContext.document.querySelectorAll<HTMLElement>(selector)[i];
        return element;
      }
    } else {
      return match;
    }
    return null;
  };

  /**
   * Insert widget based on the configuration
   * @param insert
   * @param selector
   * @param widgetSelector
   * @param usesClass
   * @param articleNumber
   * @param articleConfig
   * @param articleIndex
   */
  private static insertWidget = async (
    insert: 'inside' | 'prependInside' | 'before' | 'after',
    selector: string | ((index: string) => any) | string[],
    widgetSelector: string,
    usesClass: boolean = false,
    articleNumber: number,
    articleConfig: IArticleConfig,
    articleIndex: string
  ) => {
    let widgetDivLocation;

    if (typeof selector === 'string') {
      widgetDivLocation = articleConfig.endlessMode
        ? WidgetsCreator.getLocation(selector, widgetSelector, usesClass, articleNumber)
        : await waitForElement(selector, 200, 25);
    } else if (typeof selector === 'function') {
      widgetDivLocation = selector(articleIndex);
    } else {
      for (const selectorItem of selector) {
        const possibleLocation = articleConfig.endlessMode
          ? WidgetsCreator.getLocation(selectorItem, widgetSelector, usesClass, articleNumber)
          : await waitForElement(selectorItem, 200, 25);
        if (possibleLocation) {
          widgetDivLocation = possibleLocation;
          break;
        }
      }
    }

    if (widgetDivLocation) {
      if (
        (usesClass && widgetDivLocation.querySelector(`.${widgetSelector}`)) ||
        (!usesClass && widgetDivLocation.querySelector(`#${widgetSelector}`))
      ) {
        return null;
      }

      const widgetDiv = document.createElement('div');
      if (usesClass) {
        widgetDiv.className = widgetSelector;
      } else {
        widgetDiv.id = widgetSelector;
      }
      switch (insert) {
        case 'after':
          widgetDivLocation.insertAdjacentElement('afterend', widgetDiv);
          break;
        case 'before':
          widgetDivLocation.insertAdjacentElement('beforebegin', widgetDiv);
          break;
        case 'prependInside':
          widgetDivLocation.insertAdjacentElement('afterbegin', widgetDiv);
          break;
        default:
          widgetDivLocation.appendChild(widgetDiv);
      }
      return widgetDiv;
    } else {
      return null;
    }
  };

  /**
   * Creates widgets inside the page using AdvancedConfig configuration
   * @param widgetConfig
   * @param articleNumber
   * @param articleConfig
   * @param articleIndex
   * @param realtimeObj
   * @param widgetsCreator
   */
  public static async createWidgets(
    widgetConfig: IAdvancedConfig['widgets'],
    articleNumber: number,
    articleConfig: IArticleConfig,
    articleIndex: string,
    widgetsCreator: WidgetsCreator
  ) {
    if (widgetConfig?.preInsertionOperation) {
      widgetConfig.preInsertionOperation();
    }

    let widgetNumber = 0;
    let commentsIframe: [HTMLDivElement, IIframeInterface] | undefined =
      widgetsCreator.comments.size > 0
        ? [[...widgetsCreator.comments.keys()][0], [...widgetsCreator.comments.values()][0]]
        : undefined;
    let iframelessComments: [HTMLDivElement, IIframelessWidgetInterface] | undefined =
      widgetsCreator.iframelessComments.size > 0
        ? [[...widgetsCreator.iframelessComments.keys()][0], [...widgetsCreator.iframelessComments.values()][0]]
        : undefined;
    let emotesIframe: [HTMLDivElement, IIframeInterface] | undefined =
      widgetsCreator.emotes.size > 0
        ? [[...widgetsCreator.emotes.keys()][0], [...widgetsCreator.emotes.values()][0]]
        : undefined;
    let sharebars: [HTMLDivElement[], IIframeInterface[]] | [] =
      widgetsCreator.powerbars.size > 0
        ? [[...widgetsCreator.powerbars.keys()], [...widgetsCreator.powerbars.values()]]
        : [];
    let subscribeWidgets: [HTMLDivElement[], IIframeInterface[]] | [] =
      widgetsCreator.subscribeWidgets.size > 0
        ? [[...widgetsCreator.subscribeWidgets.keys()], [...widgetsCreator.subscribeWidgets.values()]]
        : [];
    let audioWidgets: [HTMLDivElement[], IIframeInterface[]] | [] =
      widgetsCreator.audioWidgets.size > 0
        ? [[...widgetsCreator.audioWidgets.keys()], [...widgetsCreator.audioWidgets.values()]]
        : [];
    let quizWidget: [HTMLDivElement, IIframeInterface] | undefined =
      widgetsCreator.quiz.size > 0
        ? [[...widgetsCreator.quiz.keys()][0], [...widgetsCreator.quiz.values()][0]]
        : undefined;

    /** Initialize widgets */
    if (widgetConfig?.comments && articleConfig.comments.useIframe) {
      const commentsDiv = await this.insertWidget(
        widgetConfig.comments.insert,
        Session.isMobileUser && widgetConfig.comments.mobileSelector
          ? widgetConfig.comments.mobileSelector
          : widgetConfig.comments.selector,
        widgetsConfig.selectors.comments.slice(1).concat(articleIndex),
        false,
        articleNumber,
        articleConfig,
        articleIndex
      );
      if (commentsDiv) {
        commentsIframe = await this.createCommentsWidget(articleIndex, articleConfig);
        widgetNumber++;
      }
    }

    if (widgetConfig?.comments && !articleConfig.comments.useIframe) {
      const commentsDiv = await this.insertWidget(
        widgetConfig.comments.insert,
        Session.isMobileUser && widgetConfig.comments.mobileSelector
          ? widgetConfig.comments.mobileSelector
          : widgetConfig.comments.selector,
        widgetsConfig.selectors.comments.slice(1).concat(articleIndex),
        false,
        articleNumber,
        articleConfig,
        articleIndex
      );
      if (commentsDiv) {
        iframelessComments = await this.createIframelessCommentsWidget(articleIndex, articleConfig);
        widgetNumber++;
      }
    }

    if (widgetConfig?.emotes) {
      const emotesDiv = await this.insertWidget(
        widgetConfig.emotes.insert,
        Session.isMobileUser && widgetConfig.emotes.mobileSelector
          ? widgetConfig.emotes.mobileSelector
          : widgetConfig.emotes.selector,
        widgetsConfig.selectors.emotes.slice(1).concat(articleIndex),
        false,
        articleNumber,
        articleConfig,
        articleIndex
      );
      if (emotesDiv) {
        emotesIframe = this.createEmotesIframe(articleIndex, articleConfig);
        widgetNumber++;
      }
    }

    if (widgetConfig?.sharebar) {
      widgetConfig?.sharebar.forEach(async (sharebarConfig) => {
        const sharebarDiv = await this.insertWidget(
          sharebarConfig.insert,
          Session.isMobileUser && sharebarConfig.mobileSelector
            ? sharebarConfig.mobileSelector
            : sharebarConfig.selector,
          sharebarConfig.isVertical
            ? widgetsConfig.selectors.verticalSharebar.slice(1).concat(articleIndex)
            : widgetsConfig.selectors.sharebar.slice(1).concat(articleIndex),
          true,
          articleNumber,
          articleConfig,
          articleIndex
        );

        if (sharebarDiv) {
          sharebars = await this.createPowerbars(
            articleIndex,
            !!commentsIframe?.[1] || !!iframelessComments?.[1],
            !!emotesIframe?.[1],
            articleConfig
          );
          widgetNumber++;
        }
      });
    }

    if (widgetConfig?.audio) {
      widgetConfig.audio.forEach(async (audioConfig) => {
        const audioDiv = await this.insertWidget(
          audioConfig.insert,
          Session.isMobileUser && audioConfig.mobileSelector ? audioConfig.mobileSelector : audioConfig.selector,
          widgetsConfig.selectors.audio.slice(1).concat(articleIndex),
          true,
          articleNumber,
          articleConfig,
          articleIndex
        );
        if (audioDiv) {
          audioWidgets = await this.createAudioWidgets(articleIndex, articleConfig);
          widgetNumber++;
        }
      });
    }

    if (widgetConfig?.subscribe) {
      widgetConfig.subscribe.forEach(async (subscribeConfig) => {
        const subscribeDiv = await this.insertWidget(
          subscribeConfig.insert,
          Session.isMobileUser && subscribeConfig.mobileSelector
            ? subscribeConfig.mobileSelector
            : subscribeConfig.selector,
          widgetsConfig.selectors.subscribe.slice(1).concat(articleIndex),
          true,
          articleNumber,
          articleConfig,
          articleIndex
        );
        if (subscribeDiv) {
          subscribeWidgets = await this.createSubscribeWidgets(articleIndex, articleConfig);
          widgetNumber++;
        }
      });
    }

    if (widgetConfig?.newsfeed) {
      const newsfeedDiv = await this.insertWidget(
        widgetConfig.newsfeed.insert,
        Session.isMobileUser && widgetConfig.newsfeed.mobileSelector
          ? widgetConfig.newsfeed.mobileSelector
          : widgetConfig.newsfeed.selector,
        widgetsConfig.selectors.newsfeed.slice(1).concat(articleIndex),
        false,
        articleNumber,
        articleConfig,
        articleIndex
      );
      if (newsfeedDiv) {
        WidgetsCreator.addNewsfeedToDOM(articleIndex);
        widgetNumber++;
      }
    }

    if (widgetConfig?.quiz) {
      const quizDiv = await this.insertWidget(
        widgetConfig.quiz.insert,
        Session.isMobileUser && widgetConfig.quiz.mobileSelector
          ? widgetConfig.quiz.mobileSelector
          : widgetConfig.quiz.selector,
        widgetsConfig.selectors.quiz.slice(1).concat(articleIndex),
        false,
        articleNumber,
        articleConfig,
        articleIndex
      );
      if (quizDiv) {
        quizWidget = await this.createQuizWidget(articleIndex, articleConfig);
        widgetNumber++;
      }
    }

    /** Proceed with creating communication and custom text instances if there's at least 1 widget that can be created */
    if (widgetNumber > 0) {
      widgetsCreator.setWidgetInstances(
        commentsIframe,
        iframelessComments,
        emotesIframe,
        sharebars,
        subscribeWidgets,
        audioWidgets,
        quizWidget
      );
      widgetsCreator.initializeInsideDom();
      const createCommunicationListenerInstance = async () => {
        // Remove message listeners before recreating most of them
        widgetsCreator.communicationCreator.removeMessageListeners();
        // 💬 Attach events listeners
        widgetsCreator.setCommunicationCreatorInstance(
          await CommunicationCreator.createCommunicationCreatorInstance(
            articleIndex,
            commentsIframe?.[1],
            iframelessComments?.[1],
            emotesIframe?.[1],
            sharebars[1] ? sharebars[1] : [],
            subscribeWidgets[1] ? subscribeWidgets[1] : [],
            audioWidgets[1] ? audioWidgets[1] : [],
            quizWidget?.[1],
            articleConfig,
            widgetsCreator
          )
        );
        // Pass the widgets for loading custom translation data if any
        const customText = await import(/* webpackChunkName: "cst" */ './CustomText');
        widgetsCreator.customText = new customText.default(
          commentsIframe?.[1],
          iframelessComments?.[1],
          emotesIframe?.[1],
          sharebars[1] ? sharebars[1] : [],
          quizWidget?.[1],
          articleConfig
        );
      };
      /**
       * Communication creator executes async code in it's constructor and often at this stage it may not be ready yet
       * Which is why we wait until it's ready before initializing a new communication creator instance
       */
      if (!widgetsCreator.communicationCreator) {
        const interval = setInterval(async () => {
          if (widgetsCreator.communicationCreator) {
            clearInterval(interval);
            await createCommunicationListenerInstance();
          }
        }, 100);
      } else {
        await createCommunicationListenerInstance();
      }
    }

    if (widgetConfig?.postInsertionOperation) {
      widgetConfig.postInsertionOperation();
    }
  }

  public moveQuizWidget(config: IAdvancedConfig['moveQuizzly']) {
    let widgetDivLocation;
    const { selector, mobileSelector, insert } = config!;

    const chosenSelector = Session.isMobileUser && mobileSelector ? mobileSelector : selector;

    if (typeof chosenSelector === 'string') {
      widgetDivLocation = this.articleConfig.endlessMode
        ? this.getElementLocation(chosenSelector)
        : document.querySelector(chosenSelector);
    } else if (typeof chosenSelector === 'function') {
      widgetDivLocation = chosenSelector(this.articleIndex);
    } else {
      for (const selectorItem of chosenSelector) {
        const possibleLocation = this.articleConfig.endlessMode
          ? this.getElementLocation(selectorItem)
          : document.querySelector(selectorItem);
        if (possibleLocation) {
          widgetDivLocation = possibleLocation;
          break;
        }
      }
    }

    if (widgetDivLocation) {
      const [quizValues] = this.quiz;
      const [quizDiv] = quizValues;
      switch (insert) {
        case 'after':
          widgetDivLocation.insertAdjacentElement('afterend', quizDiv);
          break;
        case 'before':
          widgetDivLocation.insertAdjacentElement('beforebegin', quizDiv);
          break;
        case 'prependInside':
          widgetDivLocation.insertAdjacentElement('afterbegin', quizDiv);
          break;
        default:
          widgetDivLocation.appendChild(quizDiv);
      }
    }
  }

  /**
   * Creates emotes iframe and adds to the this.emotes Map
   * @param {string} articleIndex - widget index
   * @param articleConfig
   * @returns {void}
   */
  private static createEmotesIframe(
    articleIndex: string,
    articleConfig: IArticleConfig
  ): [HTMLDivElement, IIframeInterface] | undefined {
    if (articleConfig.emotes && articleConfig.emotes.enabled) {
      const emotesWrapper = document.querySelector(widgetsConfig.selectors.emotes.concat(String(articleIndex) || ''));
      if (emotesWrapper instanceof HTMLDivElement) {
        emotesWrapper.style.cssText += 'margin: auto';
        const emotesIframe = new EmotesIframe('emotes-iframe'.concat(articleIndex), articleConfig);
        return [emotesWrapper, emotesIframe];
      }
    }
    return;
  }

  /**
   * Creates quiz widget and adds to the this.quiz Map
   * @param {string} articleIndex - widget index
   * @param articleConfig
   * @returns {void}
   */
  private static async createQuizWidget(
    articleIndex: string,
    articleConfig: IArticleConfig
  ): Promise<[HTMLDivElement, IIframeInterface] | undefined> {
    if (articleConfig.quiz && articleConfig.quiz.enabled) {
      const quizWrapper =
        document.querySelector<HTMLDivElement>(widgetsConfig.selectors.quiz.concat(String(articleIndex) || '')) ||
        document.querySelector<HTMLDivElement>(widgetsConfig.selectors.quizAlt.concat(String(articleIndex) || ''));
      if (quizWrapper) {
        return [quizWrapper, new QuizIframe('quiz-iframe'.concat(articleIndex), articleConfig)];
      }
    }
    return;
  }

  /**
   * Creates subscribe widget iframe and adds to the this.subscribeWidgets Map
   * @param {string} articleIndex - widget index
   * @param articleConfig
   * @returns {void}
   */
  private static async createSubscribeWidgets(
    articleIndex: string,
    articleConfig: IArticleConfig
  ): Promise<[HTMLDivElement[], IIframeInterface[]] | []> {
    if (articleConfig.subscribe && articleConfig.subscribe.enabled) {
      const subscribeWrappers = document.querySelectorAll<HTMLDivElement>(
        widgetsConfig.selectors.subscribe.concat(String(articleIndex) || '')
      );
      const subscribeDivs = [];
      const subscribeIframes = [];
      for (let i = 0; i < subscribeWrappers.length; i++) {
        /** Subscribe widgets need unique ids which is why the array index is concatenated */
        const subscribeIframe = await import(/* webpackChunkName: 'sbi' */ './iframes/Subscribe');

        subscribeDivs.push(subscribeWrappers[i]);
        subscribeIframes.push(
          new subscribeIframe.default('subscribe-widget'.concat(articleIndex).concat(`-${i}`), articleConfig)
        );
      }
      return [subscribeDivs, subscribeIframes];
    }
    return [];
  }

  /**
   * Creates audio widget iframe and adds to the this.audioWidgets Map
   * @param {string} articleIndex - widget index
   * @param articleConfig
   * @returns {void}
   */
  private static async createAudioWidgets(
    articleIndex: string,
    articleConfig: IArticleConfig
  ): Promise<[HTMLDivElement[], IIframeInterface[]] | []> {
    if (articleConfig.audio && articleConfig.audio.enabled) {
      const audioWrappers = document.querySelectorAll<HTMLDivElement>(
        widgetsConfig.selectors.audio.concat(String(articleIndex) || '')
      );
      const audioDivs = [];
      const audioIframes = [];
      for (let i = 0; i < audioWrappers.length; i++) {
        /** Audio widgets need unique ids which is why the array index is concatenated */
        const audioIframe = await import(/* webpackChunkName: 'awi' */ './iframes/Audio');
        audioDivs.push(audioWrappers[i]);
        audioIframes.push(new audioIframe.default('audio-widget'.concat(articleIndex).concat(`-${i}`), articleConfig));
      }
      return [audioDivs, audioIframes];
    }
    return [];
  }

  /**
   * Creates vertical and horizontal mode powerbars and adds to appropriate Map (this.powerbars, this.verticalPowerbars)
   * @param {string} articleIndex - widgets index
   * @param {boolean} withCommentsWidget - comments widget is available/created. Needed for standaloneMode {@see standaloneModeType}
   * @param {boolean} withEmotesWidget - emotes widget is available/created. Needed for standaloneMode {@see standaloneModeType}
   * @param articleConfig
   * @returns {void}
   */
  private static async createPowerbars(
    articleIndex: string,
    withCommentsWidget: boolean,
    withEmotesWidget: boolean,
    articleConfig: IArticleConfig
  ): Promise<[HTMLDivElement[], IIframeInterface[]] | []> {
    // 🔴 We don't need to create widgets if they are disabled
    if (
      (articleConfig.powerbar && articleConfig.powerbar.enabled === false) ||
      (articleConfig.sharebar && articleConfig.sharebar.enabled === false)
    ) {
      return [];
    }
    /** Collect all needed powerbars on the page */
    const powerbars = Array.prototype.slice.call(
      document.querySelectorAll(widgetsConfig.selectors.powerbar.concat(String(articleIndex)))
    );
    const sharebars = Array.prototype.slice.call(
      document.querySelectorAll(widgetsConfig.selectors.sharebar.concat(String(articleIndex)))
    );
    const verticalPowerbars = Array.prototype.slice.call(
      document.querySelectorAll(widgetsConfig.selectors.verticalPowerbar.concat(String(articleIndex)))
    );
    const verticalSharebars = Array.prototype.slice.call(
      document.querySelectorAll(widgetsConfig.selectors.verticalSharebar.concat(String(articleIndex)))
    );

    const sharebarDivs = [];
    const sharebarIframes = [];

    /** Create horizontal powerbars iFrames */
    for (let i = 0; i < powerbars.length; i++) {
      const powerbarIframe = await import(/* webpackChunkName: 'pbi' */ './iframes/Powerbar');
      sharebarDivs.push(powerbars[i]);
      sharebarIframes.push(
        new powerbarIframe.default(
          // sharebar needs additional index because there can be several sharebars and they need to have
          // unique ID
          'h-sharebar'.concat(articleIndex).concat(`-${i}`),
          articleConfig,
          'horizontal',
          withCommentsWidget,
          withEmotesWidget
        )
      );
    }

    for (let i = 0; i < sharebars.length; i++) {
      const powerbarIframe = await import(/* webpackChunkName: 'pbi' */ './iframes/Powerbar');

      sharebarDivs.push(sharebars[i]);
      sharebarIframes.push(
        new powerbarIframe.default(
          // sharebar needs additional index because there can be several sharebars and they need to have
          // unique ID
          'h-sharebar'.concat(articleIndex).concat(`-${i}`),
          articleConfig,
          'horizontal',
          withCommentsWidget,
          withEmotesWidget
        )
      );
    }
    for (let i = 0; i < verticalPowerbars.length; i++) {
      // 💅 Add default styles for vertical powerbar wrapper
      verticalPowerbars[i].style.zIndex = '99999';
      verticalPowerbars[i].style.width = '60px';
      verticalPowerbars[i].style.maxWidth = '60px';
      verticalPowerbars[i].style.position = verticalPowerbars[i].style.position || 'fixed';
      verticalPowerbars[i].style.top = verticalPowerbars[i].style.top || '10px';

      if (Session.isMobileUser) {
        verticalPowerbars[i].style.right = '0';
        verticalPowerbars[i].style.left = 'unset';
      }
      const powerbarIframe = await import(/* webpackChunkName: 'pbi' */ './iframes/Powerbar');

      sharebarDivs.push(verticalPowerbars[i]);
      sharebarIframes.push(
        new powerbarIframe.default(
          'v-sharebar'.concat(articleIndex).concat(`-${i}`),
          articleConfig,
          'vertical',
          withCommentsWidget,
          withEmotesWidget
        )
      );
    }

    for (let i = 0; i < verticalSharebars.length; i++) {
      // 💅 Add default styles for vertical powerbar wrapper
      verticalSharebars[i].style.zIndex = '99999';
      verticalSharebars[i].style.width = '60px';
      verticalSharebars[i].style.maxWidth = '60px';
      verticalSharebars[i].style.position = verticalSharebars[i].style.position || 'fixed';
      verticalSharebars[i].style.top = verticalSharebars[i].style.top || '10px';

      if (Session.isMobileUser) {
        verticalSharebars[i].style.right = '0';
        verticalSharebars[i].style.left = 'unset';
      }

      const powerbarIframe = await import(/* webpackChunkName: 'pbi' */ './iframes/Powerbar');

      sharebarDivs.push(verticalSharebars[i]);
      sharebarIframes.push(
        new powerbarIframe.default(
          'v-sharebar'.concat(articleIndex).concat(`-${i}`),
          articleConfig,
          'vertical',
          withCommentsWidget,
          withEmotesWidget
        )
      );
    }
    return [sharebarDivs, sharebarIframes];
  }

  /**
   * Creates an iframeless comments instance
   * @param {string} articleIndex - widget index to add to selector
   * @param articleConfig
   * @returns {void}
   */
  private static async createIframelessCommentsWidget(
    articleIndex: string,
    articleConfig: IArticleConfig
  ): Promise<[HTMLDivElement, IIframelessWidgetInterface] | undefined> {
    // 🔴 We don't need to create comments widget if it is disabled
    if (articleConfig.comments.useIframe || (articleConfig.comments && articleConfig.comments.enabled === false)) {
      return;
    }

    const commentsWidgetWrapper = document.querySelector(widgetsConfig.selectors.comments.concat(articleIndex));

    if (commentsWidgetWrapper instanceof HTMLDivElement) {
      // 💅 Add needed styles to the wrapper element
      commentsWidgetWrapper.style.cssText +=
        'display: flex;flex-direction: column;width: 100%;align-items: stretch;overflow-anchor: auto;';
      if (commentsWidgetWrapper.parentElement) {
        commentsWidgetWrapper.parentElement.style.cssText += 'overflow-anchor: auto';
      }
      return [
        commentsWidgetWrapper,
        new IframelessComments(
          'v-comments-iframeless'.concat(articleIndex),
          commentsWidgetWrapper,
          articleConfig,
          articleIndex
        ),
      ];
    }
    return;
  }

  /**
   * Creates comments iFrame, and adds to appropriate Map
   * @param {string} articleIndex - widget index to add to selector
   * @param articleConfig
   * @returns {void}
   */
  private static async createCommentsWidget(
    articleIndex: string,
    articleConfig: IArticleConfig
  ): Promise<[HTMLDivElement, IIframeInterface] | undefined> {
    // 🔴 We don't need to create comments widget if it is disabled
    if (!articleConfig.comments.useIframe || (articleConfig.comments && articleConfig.comments.enabled === false)) {
      return;
    }

    const commentsWidgetWrapper = document.querySelector(widgetsConfig.selectors.comments.concat(articleIndex));

    if (commentsWidgetWrapper instanceof HTMLDivElement) {
      // 💅 Add needed styles to the wrapper element
      commentsWidgetWrapper.style.cssText +=
        'display: flex;flex-direction: column;width: 100%;align-items: stretch;overflow-anchor: auto;';
      if (commentsWidgetWrapper.parentElement) {
        commentsWidgetWrapper.parentElement.style.cssText += 'overflow-anchor: auto';
      }
      return [commentsWidgetWrapper, new Comments('v-comments'.concat(articleIndex), articleConfig, articleIndex)];
    }
    return;
  }

  /**
   * Creates modal widget and adds to appropriate Map
   * @param data
   * @returns {void}
   */
  public async createModal(data: any): Promise<void> {
    const body = document.querySelector('body');
    const wrapper = document.createElement('div');
    if (body instanceof HTMLBodyElement) {
      body.appendChild(wrapper);
      const modalIframe = await import(/* webpackChunkName: 'mdi' */ './iframes/Modal');
      this.modal.set(wrapper, new modalIframe.default('vuukle-modal'.concat(this.articleIndex), this.articleConfig));

      await this.communicationCreator.initializeModal([...this.modal.values()][0], data);
      this.customText.initializeModal([...this.modal.values()][0]);

      // Initialize Modal Inside Dom
      this.modal.forEach((iframeObj, div) => div.appendChild(iframeObj.iframe));
    }
  }

  /**
   * ⚠ Instantly adds newsfeed widget to the DOM as it's not iframe widget
   * @param {string} articleIndex - widgets index
   * @returns {void}
   */
  private static addNewsfeedToDOM(articleIndex: string): void {
    const newsFeedWrapper = document.querySelector(widgetsConfig.selectors.newsfeed.concat(articleIndex));
    if (newsFeedWrapper instanceof HTMLDivElement) {
      const newsFeedMountNode = document.createElement('div');
      newsFeedMountNode.id = 'vuukle-newsfeed-app'; // 🔴 This is what `newsfeed.js` will use as mount node

      newsFeedWrapper.appendChild(newsFeedMountNode);
      loadJS(widgetsConfig.urls.newsfeed);
    }
  }

  /**
   * ⚠ Instantly adds traffic exchange widget to the DOM as it's not iframe widget
   * @param {string} articleIndex - widgets index
   * @param apiKey
   * @returns {void}
   */
  private static addTrafficExchangeToDOM(articleIndex: string, apiKey: string): void {
    const trafficExchangeWrapper = document.querySelector(widgetsConfig.selectors.trafficExchange.concat(articleIndex));
    if (trafficExchangeWrapper instanceof HTMLDivElement) {
      const trafficExchangeNode = document.createElement('div');
      trafficExchangeNode.id = 'vuukle-traffic-exchange-app'; // 🔴 This is what `trafficexchange.js` will use as mount node
      // Configure article row number if specified in the config
      if (advancedKeys[apiKey] && advancedKeys[apiKey].trafficExchangeRows) {
        trafficExchangeNode.dataset.rows = advancedKeys[apiKey].trafficExchangeRows;
      }

      trafficExchangeWrapper.appendChild(trafficExchangeNode);
      loadJS(widgetsConfig.urls.trafficExchange);
    }
  }

  /**
   * Intersection Observer is used to lazy load the widgets once they're a tiny bit visible in user viewport
   * @param widgetObject - The iframe or widget object of the widget
   * @param observableElement - The wrapper div of the widget
   */
  private attachIObserver = (
    widgetObject: IIframeInterface | IIframelessWidgetInterface,
    observableElement: HTMLElement
  ) => {
    const intersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          const { intersectionRatio, isIntersecting } = entry;
          const isInView = intersectionRatio > 0.025 || isIntersecting;
          if (isInView) {
            if ('loadIframe' in widgetObject) {
              widgetObject.loadIframe();
            }
            if ('loadWidget' in widgetObject && !widgetObject.widgetLoaded) {
              widgetObject.loadWidget();
            }
            intersectionObserver.disconnect();
          }
        });
      },
      { threshold: 0.02, ...widgetObject.intersectionObserverOptions }
    );
    intersectionObserver.observe(observableElement);
  };

  /**
   * Adds widgets to the DOM based on priority
   * @returns {void}
   */
  private initializeInsideDom(): void {
    // Comments 1st prior
    this.comments.forEach((iframeObj, div) => {
      if (!div.querySelector('iframe[id*="v-comments"]')) {
        // this.attachIObserver(iframeObj, div);
        // div.appendChild(iframeObj.iframeSkeleton);
        div.appendChild(iframeObj.iframe);
      }
    });
    // this.iframelessComments.forEach((widgetObj, div) => {
    //   this.attachIObserver(widgetObj, div);
    // });
    // Emotes 2nd prior
    this.emotes.forEach((iframeObj, div) => {
      if (!div.querySelector('iframe[id*="emotes-iframe"]')) {
        // this.attachIObserver(iframeObj, div);
        // div.appendChild(iframeObj.iframeSkeleton);
        div.appendChild(iframeObj.iframe);
      }
    });
    // Quiz 3rd prior
    this.quiz.forEach((iframeObj, div) => {
      if (!div.querySelector('iframe[id*="quiz-iframe"]')) {
        // this.attachIObserver(iframeObj, div);
        div.appendChild(iframeObj.iframe);
      }
    });
    // Sharebars 4th prior
    this.powerbars.forEach((iframeObj, div) => {
      if (!div.querySelector('iframe[id*="h-sharebar"]')) {
        this.attachIObserver(iframeObj, div);
        // div.appendChild(iframeObj.iframeSkeleton);
        div.appendChild(iframeObj.iframe);
      }
    });
    this.verticalPowerbars.forEach((iframeObj, div) => {
      if (!div.querySelector('iframe[id*="v-sharebar"]')) {
        this.attachIObserver(iframeObj, div);
        div.appendChild(iframeObj.iframe);
      }
    });
    this.subscribeWidgets.forEach((iframeObj, div) => {
      if (!div.querySelector('iframe[id*="subscribe-widget"]')) {
        this.attachIObserver(iframeObj, div);
        // div.appendChild(iframeObj.iframeSkeleton);
        div.appendChild(iframeObj.iframe);
      }
    });
    this.audioWidgets.forEach((iframeObj, div) => {
      if (!div.querySelector('iframe[id*="audio-widget"]')) {
        this.attachIObserver(iframeObj, div);
        div.appendChild(iframeObj.iframe);
      }
    });
  }

  /**
   * Remove generated widgets by ID
   * @param ID The element index
   */
  public static removeWidgets(ID: string) {
    // Take out the id and select all the widgets
    const indexOfElement: number = this.initializedIDs.indexOf(ID);

    if (indexOfElement === -1) {
      debugLog('log', `[VUUKLE] Widgets of id '${ID.substring(1)}' aren't generated, nothing to remove`);
      return;
    }

    this.initializedIDs.splice(indexOfElement, 1);
    const comments = document.querySelector(`iframe[id*="v-comments${ID}"]`);
    const modal = document.querySelector(`iframe[id*="vuukle-modal${ID}"]`);
    const emotes = document.querySelector(`iframe[id*="emotes-iframe${ID}"]`);
    const powerbars = document.querySelectorAll(`iframe[id*="h-powerbar${ID}"]`);
    const verticalPowerbars = document.querySelectorAll(`iframe[id*="v-powerbar${ID}"]`);
    const sharebars = document.querySelectorAll(`iframe[id*="h-sharebar${ID}"]`);
    const verticalSharebars = document.querySelectorAll(`iframe[id*="v-sharebar${ID}"]`);
    const subscribeWidgets = document.querySelectorAll(`iframe[id*="subscribe-widget${ID}"]`);
    const audioWidgets = document.querySelectorAll(`iframe[id*="audio-widget${ID}"]`);
    const quiz = document.querySelector(`iframe[id*="quiz-iframe${ID}"]`);

    // Now that widgets are selected, check if they're on the page and remove them all
    if (comments) {
      comments.remove();
    }

    if (modal) {
      modal.remove();
    }

    if (emotes) {
      emotes.remove();
    }

    if (powerbars) {
      powerbars.forEach((bar) => bar.remove());
    }

    if (verticalPowerbars) {
      verticalPowerbars.forEach((vbar) => vbar.remove());
    }

    if (sharebars) {
      sharebars.forEach((bar) => bar.remove());
    }

    if (verticalSharebars) {
      verticalSharebars.forEach((bar) => bar.remove());
    }

    if (subscribeWidgets) {
      subscribeWidgets.forEach((subscribe) => subscribe.remove());
    }

    if (audioWidgets) {
      audioWidgets.forEach((audio) => audio.remove());
    }

    if (quiz) {
      quiz.remove();
    }

    Session.removeCookieListenerByIndex(ID);
    Session.removeGeoListenersByIndex(ID);
    ServerLogger.removeIPListenersByIndex(ID);
    CommunicationCreator.removeWidgetsFromThemingObject(ID);
    CommunicationCreator.quizWidgetsOnThePage.delete(ID);

    debugLog('log', `[VUUKLE] Widgets of id '${ID.substring(1)}' are removed!`);
  }
}

export default WidgetsCreator;
